import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { CONNECTION_STATE_CHANGE, ConnectionState, generateClient } from 'aws-amplify/api';
import { Hub } from 'aws-amplify/utils';
import type { Observable } from 'rxjs';

import { ErrorTypeEnum } from '../../constants/error-type.ts';

import { getAwsAuthUtil } from '../aws/aws-auth.util.ts';
import { getSentryUtil } from '../monitoring/sentry.util.ts';
import { getReduxStoreUtil } from '../redux-store.util.ts';

export class ReadingCrittersRealtimeClientUtil {
  private static _instance: ReadingCrittersRealtimeClientUtil;
  private readonly CLIENT_NAME = 'ReadingCrittersRealtimeClientUtil';
  private client = generateClient();

  private constructor(
    private readonly authUtil = getAwsAuthUtil(),
    private readonly sentryUtil = getSentryUtil()
  ) {}

  static get instance(): ReadingCrittersRealtimeClientUtil {
    if (!ReadingCrittersRealtimeClientUtil._instance) {
      ReadingCrittersRealtimeClientUtil._instance = new ReadingCrittersRealtimeClientUtil();
    }
    return ReadingCrittersRealtimeClientUtil._instance;
  }

  public async subscribe<R, V extends object | undefined>(
    doc: TypedDocumentNode<R, V>,
    variables: V,
    hooks: {
      onConnected: () => void;
      onData?: (data: R) => void;
      onError?: (error: Error) => void;
    },
    options: {
      useAuth: boolean;
    }
  ): Promise<{
    onClose: () => Promise<void> | void;
  }> {
    try {
      const store = await getReduxStoreUtil().getRootState();
      const env = store.environment.selectedEnvironment;
      const authToken = options.useAuth ? await this.authUtil.getIdToken() : undefined;

      const observable = await this.client.graphql({
        // @ts-expect-error aws-amplify uses an older version of the graphql package that causes this error
        query: doc,
        variables,
        authToken,
        endpoint: env?.apiUrl,
      });
      const usableObservable = observable as Observable<{ data: R }>;
      // The promise above can resolve before the connection is established
      // For this reason, we need to listen for the connection state change
      // Sadly, we can't know for sure if this is the connection that we just established
      // We should look into it and see if there's a better way to handle this
      const cancel = Hub.listen(
        'api',
        (data: { payload: { event: string; data: { connectionState: ConnectionState } } }) => {
          const { payload } = data;
          if (payload.event === CONNECTION_STATE_CHANGE) {
            const { connectionState } = payload.data;
            if (connectionState === ConnectionState.Connected) {
              hooks.onConnected();
              cancel();
            }
          }
        }
      );

      // Probably due to the type error above, the type of the observable is not properly inferred
      const subscription = usableObservable.subscribe({
        next: ({ data }: { data: R }) => {
          hooks.onData?.(data);
        },
        error: (error: Error) => {
          this.handleSubscriptionError(error);
          subscription.unsubscribe();
          hooks.onError?.(error);
        },
      });

      return {
        onClose: (): void => {
          subscription.unsubscribe();
        },
      };
    } catch (error) {
      const networkError = this.handleNetworkError(error as Error);
      hooks.onError?.(networkError);
    }
    return {
      onClose: (): void => {
        // Do nothing
      },
    };
  }

  private handleSubscriptionError(error: Error): void {
    const enhancedError = new Error(error.message);
    enhancedError.name = ErrorTypeEnum.GRAPHQL;

    this.sentryUtil.trackError(enhancedError, {
      client: this.CLIENT_NAME,
      errorType: ErrorTypeEnum.GRAPHQL,
    });
  }

  private handleNetworkError(error: Error): Error {
    const networkError = new Error((error as { message: string }).message);
    networkError.name = `${ErrorTypeEnum.GRAPHQL_NETWORK}`;

    this.sentryUtil.trackError(networkError, {
      client: this.CLIENT_NAME,
      errorType: ErrorTypeEnum.GRAPHQL_NETWORK,
    });

    return networkError;
  }
}

export const getReadingCrittersRealtimeClientUtil = (): ReadingCrittersRealtimeClientUtil =>
  ReadingCrittersRealtimeClientUtil.instance;
